import { useId } from 'react';
import { FiCalendar, FiX } from 'react-icons/fi';
import {
    PLAN_TYPES,
    SUBSCRIPTION_STATUSES,
    type AnalyticsFilters,
    type AnalyticsOption,
    type PeriodPreset,
    type PlanType,
    type SubscriptionStatus,
} from '../../services/analytics';
import { DEFAULT_FILTERS, hasActiveFilters } from './analyticsPage';
import { formatDayBR, formatInt } from './format';
import styles from './AnalyticsFiltersBar.module.css';

const PERIOD_LABELS: Record<PeriodPreset, string> = {
    today: 'Hoje',
    yesterday: 'Ontem',
    last7d: 'Últimos 7 dias',
    monthToDate: 'Mês até hoje',
    previousMonth: 'Mês anterior',
    yearToDate: 'Ano até hoje',
    custom: 'Personalizado',
};

const PLAN_LABELS: Record<PlanType, string> = {
    monthly: 'Mensal',
    yearly: 'Anual',
    free: 'Gratuito',
};

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
    ativa: 'Ativa',
    em_atraso: 'Em atraso',
    gratuita: 'Gratuita',
    suspensa: 'Suspensa',
    cancelada: 'Cancelada',
    reembolsada: 'Reembolsada',
};

const PERIOD_OPTIONS: AnalyticsOption[] = (Object.keys(PERIOD_LABELS) as PeriodPreset[]).map((value) => ({
    value,
    label: PERIOD_LABELS[value],
}));
const PLAN_OPTIONS: AnalyticsOption[] = PLAN_TYPES.map((value) => ({ value, label: PLAN_LABELS[value] }));
const STATUS_OPTIONS: AnalyticsOption[] = SUBSCRIPTION_STATUSES.map((value) => ({
    value,
    label: STATUS_LABELS[value],
}));

interface FieldProps {
    label: string;
    value: string;
    options: AnalyticsOption[];
    onChange: (value: string) => void;
    /** Rotulo da opcao vazia. Sem ele, o campo nao tem opcao "Todos" (periodo). */
    allLabel?: string;
}

function SelectField({ label, value, options, onChange, allLabel }: FieldProps) {
    const id = useId();
    return (
        <div className={styles.field}>
            <label htmlFor={id} className={styles.label}>
                {label}
            </label>
            <select id={id} className={styles.select} value={value} onChange={(e) => onChange(e.target.value)}>
                {allLabel && <option value="">{allLabel}</option>}
                {options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

interface AnalyticsFiltersBarProps {
    filters: AnalyticsFilters;
    onChange: (filters: AnalyticsFilters) => void;
    segmentos: AnalyticsOption[];
    faturamentos: AnalyticsOption[];
    /** Contas totais do filtro atual, mostradas no selo ao lado do titulo. */
    totalContas: number | null;
    /** Periodo efetivo devolvido pelo back-end (YYYY-MM-DD). */
    range: { from: string; to: string } | null;
    /** Maior data permitida no periodo personalizado (hoje, YYYY-MM-DD). */
    maxDay: string;
}

export function AnalyticsFiltersBar({
    filters,
    onChange,
    segmentos,
    faturamentos,
    totalContas,
    range,
    maxDay,
}: AnalyticsFiltersBarProps) {
    const fromId = useId();
    const toId = useId();
    const set = (patch: Partial<AnalyticsFilters>) => onChange({ ...filters, ...patch });
    const orUndefined = (v: string) => v || undefined;

    return (
        <section className={styles.card} aria-label="Filtros">
            <header className={styles.header}>
                <div className={styles.titleRow}>
                    <h3 className={styles.title}>Filtros</h3>
                    {totalContas !== null && (
                        <span className={styles.badge}>
                            {formatInt(totalContas)} {totalContas === 1 ? 'conta' : 'contas'}
                        </span>
                    )}
                </div>
                <div className={styles.headerRight}>
                    {range && (
                        <span className={styles.range}>
                            <FiCalendar size={14} aria-hidden="true" />
                            {formatDayBR(range.from)} a {formatDayBR(range.to)}
                        </span>
                    )}
                    <button
                        type="button"
                        className={styles.clear}
                        disabled={!hasActiveFilters(filters)}
                        onClick={() => onChange(DEFAULT_FILTERS)}
                    >
                        <FiX size={14} aria-hidden="true" />
                        Limpar filtros
                    </button>
                </div>
            </header>

            <div className={styles.grid}>
                <SelectField
                    label="Período"
                    value={filters.period}
                    options={PERIOD_OPTIONS}
                    onChange={(v) => set({ period: v as PeriodPreset })}
                />
                <SelectField
                    label="Tipo de plano"
                    value={filters.planType ?? ''}
                    options={PLAN_OPTIONS}
                    allLabel="Todos"
                    onChange={(v) => set({ planType: orUndefined(v) as PlanType | undefined })}
                />
                <SelectField
                    label="Status da assinatura"
                    value={filters.status ?? ''}
                    options={STATUS_OPTIONS}
                    allLabel="Todos"
                    onChange={(v) => set({ status: orUndefined(v) as SubscriptionStatus | undefined })}
                />
                <SelectField
                    label="Segmento declarado"
                    value={filters.segmento ?? ''}
                    options={segmentos}
                    allLabel="Todos"
                    onChange={(v) => set({ segmento: orUndefined(v) })}
                />
                <SelectField
                    label="Faturamento declarado"
                    value={filters.faturamento ?? ''}
                    options={faturamentos}
                    allLabel="Todos"
                    onChange={(v) => set({ faturamento: orUndefined(v) })}
                />
            </div>

            {filters.period === 'custom' && (
                <div className={styles.customRange}>
                    <div className={styles.field}>
                        <label htmlFor={fromId} className={styles.label}>
                            De
                        </label>
                        <input
                            id={fromId}
                            type="date"
                            className={styles.select}
                            value={filters.from ?? ''}
                            max={filters.to || maxDay}
                            onChange={(e) => set({ from: orUndefined(e.target.value) })}
                        />
                    </div>
                    <div className={styles.field}>
                        <label htmlFor={toId} className={styles.label}>
                            Até
                        </label>
                        <input
                            id={toId}
                            type="date"
                            className={styles.select}
                            value={filters.to ?? ''}
                            min={filters.from || undefined}
                            max={maxDay}
                            onChange={(e) => set({ to: orUndefined(e.target.value) })}
                        />
                    </div>
                </div>
            )}
        </section>
    );
}
